// src/UserContext.js - VERSIONE FINALE E STABILE

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { requestNotificationPermission } from './fcm';
import { getPermissionablePages } from './appConfig';
import { toast } from 'react-toastify';

const UserContext = createContext();

export function UserProvider({ children }) {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);
  const [activeCenterId, setActiveCenterId] = useState(null);
  const [availableCenters, setAvailableCenters] = useState({});

  // --- Funzione Helper per Notifiche Non-Bloccante ---
  const initNotifications = async (profile) => {
    try {
      await requestNotificationPermission(profile);
    } catch (err) {
      console.warn("Impossibile inizializzare notifiche (non critico):", err);
    }
  };

  // --- Funzione Caricamento Profilo ---
  const loadUserProfile = useCallback(async (uid, centerId) => {
    if (!uid || !centerId) return null;

    try {
      const userDocRef = doc(db, 'centers', centerId, 'staff', uid);
      const docSnap = await getDoc(userDocRef);

      if (docSnap.exists()) {
        const profileData = { uid, ...docSnap.data(), centerId };
        setUserProfile(profileData);
        setActiveCenterId(centerId);
        setForcePasswordChange(profileData.forcePasswordChange === true);
        
        // Inizializza notifiche in background (senza await bloccante)
        initNotifications(profileData);
        
        return profileData;
      } else {
        console.warn(`Profilo non trovato per ${uid} in ${centerId} — uso profilo virtuale`);

        // Logica Profilo Virtuale (User Lookup)
        const lookupRef = doc(db, 'userLookup', uid);
        const lookupSnap = await getDoc(lookupRef);

        if (!lookupSnap.exists()) {
            console.error(`Nessun userLookup trovato per ${uid}`);
            return null;
        }

        const lookupData = lookupSnap.data();
        const centersMap = lookupData.centers || {};
        let role = centersMap[centerId] || 'Utente';

        // Check Super Admin Globale
        const superAdminRef = doc(db, 'superAdmins', uid);
        const superAdminSnap = await getDoc(superAdminRef);
        if (superAdminSnap.exists()) role = 'Super Admin';

        const virtualProfile = {
            uid,
            name: lookupData.name || auth.currentUser?.displayName || 'Utente',
            email: lookupData.email || auth.currentUser?.email,
            role,
            includeInShifts: false,
            color: role === 'Super Admin' ? '#D32F2F' : '#0288d1',
            centerId,
        };

        // Permessi speciali
        if (role === 'Super Admin' || role === 'Proprietario') {
            const pages = getPermissionablePages();
            const allPermissions = {};
            pages.forEach(page => { allPermissions[page.key] = 'completo'; });
            virtualProfile.permissions = allPermissions;
        } else {
            virtualProfile.permissions = {};
        }

        setUserProfile(virtualProfile);
        setActiveCenterId(centerId);
        setForcePasswordChange(false);
        return virtualProfile;
      }
    } catch (error) {
      console.error('Errore critico in loadUserProfile:', error);
      return null;
    }
  }, []);

  // --- Main Auth Effect ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setUserProfile(null);
        setForcePasswordChange(false);
        setActiveCenterId(null);
        setAvailableCenters({});
        setLoading(false); // Sblocca subito se logout
        return;
      }

      setLoading(true); // Blocca UI durante caricamento

      try {
        // 🆕 IMPERSONIFICAZIONE (Pannello MIA): stessa logica del Gestionale
        // Clinico — vedi AuthContext.jsx per il commento completo.
        const urlParams = new URLSearchParams(window.location.search);
        const impersonateCenterId = urlParams.get('impersonate');

        if (impersonateCenterId) {
          const superAdminSnap = await getDoc(doc(db, 'superAdmins', user.uid));
          if (superAdminSnap.exists()) {
            const pages = getPermissionablePages();
            const allPermissions = {};
            pages.forEach(page => { allPermissions[page.key] = 'completo'; });

            setUserProfile({
              uid: user.uid,
              name: 'Super Admin (impersonificazione)',
              role: 'Super Admin',
              centerId: impersonateCenterId,
              includeInShifts: false,
              color: '#D32F2F',
              permissions: allPermissions,
              isImpersonating: true,
            });
            setActiveCenterId(impersonateCenterId);
            setAvailableCenters({ [impersonateCenterId]: 'Super Admin' });
            setForcePasswordChange(false);
            setLoading(false);
            return;
          }
          console.warn("Parametro ?impersonate presente ma l'utente non è Super Admin: ignorato.");
        }

        // 1. User Lookup
        const lookupRef = doc(db, 'userLookup', user.uid);
        const lookupSnap = await getDoc(lookupRef);

        if (lookupSnap.exists()) {
          const lookupData = lookupSnap.data();
          const centersMap = lookupData.centers || {};
          const defaultCenter = lookupData.defaultCenter;
          setAvailableCenters(centersMap);

          if (defaultCenter) {
            await loadUserProfile(user.uid, defaultCenter);
          } else {
             console.warn("Utente senza centro di default.");
             setUserProfile(null); // O gestisci diversamente
          }
        } else {
           // 2. Fallback Super Admin (se userLookup manca)
           const superAdminRef = doc(db, 'superAdmins', user.uid);
           const superAdminSnap = await getDoc(superAdminRef);
           
           if (superAdminSnap.exists()) {
               // Crea profilo volante Super Admin
               const pages = getPermissionablePages();
               const allPermissions = {};
               pages.forEach(page => { allPermissions[page.key] = 'completo'; });

               setUserProfile({ 
                   uid: user.uid, 
                   role: 'Super Admin', 
                   centerId: 'centro-medico-michelangelo', // Default hardcoded
                   permissions: allPermissions
               });
           } else {
               console.error("Utente non riconosciuto nel sistema.");
           }
        }
      } catch (err) {
        console.error("Errore fatale Auth:", err);
        toast.error("Errore di connessione al profilo.");
      } finally {
        setLoading(false); // Sblocca SEMPRE alla fine
      }
    });

    return () => unsubscribe();
  }, [loadUserProfile]);

  // --- Funzione Cambio Centro ---
  const switchCenter = useCallback(async (newCenterId) => {
      if (!availableCenters[newCenterId] || newCenterId === activeCenterId) return;

      setLoading(true);
      try {
          const lookupRef = doc(db, 'userLookup', userProfile.uid);
          await updateDoc(lookupRef, { defaultCenter: newCenterId });
      } catch (e) { console.warn("Update defaultCenter fallito", e); }

      await loadUserProfile(userProfile.uid, newCenterId);
      setLoading(false);
  }, [availableCenters, activeCenterId, userProfile, loadUserProfile]);

  const passwordChanged = () => setForcePasswordChange(false);

  return (
    <UserContext.Provider value={{ userProfile, loading, forcePasswordChange, passwordChanged, activeCenterId, availableCenters, switchCenter }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}